import { useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useSearchParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import { LegendaDaEscala, MapaDoBrasilSvg, corDoDegrau } from "../componentes/MapaUi";
import {
  dataCurta,
  dataLonga,
  diasAte,
  hojeIso,
  paraBusca,
  urgenciaDoPrazo,
  UFS,
  type Situacao,
} from "../lib/concursos";
import { favoritar, idDaSessao, lerCatalogo, type Concurso } from "../lib/mapaConcursos";
import { useRolarAoEscolher } from "../lib/rolarPara";
import {
  AbasDoMapa,
  AgendaDeProvas,
  AlertaDePrazo,
  GRUPO_DE_VAGAS,
} from "../componentes/MapaConcursosUi";

// O Mapa de Concursos Contábeis (000120) — o catálogo de vagas públicas de
// contabilidade, portado do app da Lovable em 27/08/2026.
//
// ===========================================================================
// A tela abre no que DÁ PARA FAZER, e diz que faz isso
// ===========================================================================
// Medido em 27/08/2026, sobre as 256 linhas: **209 estão com a inscrição
// encerrada**. Abrir na lista inteira enterra as 32 abertas debaixo de sete
// oitavos de catálogo morto — e o aluno rola 209 linhas para descobrir isso.
//
// Então o filtro de entrada é "dá para agir" (abertas + as que vão abrir), e a
// contagem de cada chip fica À VISTA, incluindo a das encerradas. Filtro padrão
// que esconde 209 linhas sem dizer é pior que nenhum filtro: o aluno concluiria
// que o catálogo tem 40 concursos.
//
// ===========================================================================
// O mapa é o atalho; a LISTA é a navegação
// ===========================================================================
// O SVG é `aria-hidden` e o filtro por estado tem chips de verdade ao lado — a
// mesma decisão do Legaliza Fácil, e pela mesma razão medida: no celular tocar em
// Sergipe é impossível, e no teclado o `<path>` não existe.
//
// No app de origem o mapa NÃO é coroplético: são três cores categóricas
// (selecionado / tem concurso / não tem) e a quantidade aparece como texto de 6px
// dentro do contorno. Aqui a cor É a quantidade, com legenda ao lado — mapa
// pintado por quantidade sem legenda é decoração.
//
// ===========================================================================
// Lista densa, não grade de cards
// ===========================================================================
// A cicatriz do IR Pro: 8.249px viraram 1.120px com o MESMO conteúdo quando o
// índice deixou de ser grade de cards. Aqui o uso real é varrer a lista
// procurando o próprio estado e um prazo que ainda dê tempo, e para isso linha
// de texto encontra melhor que cartão.
//
// ===========================================================================
// A procedência vai na tela, junto do dado
// ===========================================================================
// "Número certo sem procedência ensina a confiar em número velho" — e aqui o
// dado é perecível por natureza. Cada linha carrega quando foi conferida, e a
// cabine diz a data da conferência mais recente do catálogo. Sem isso, edital
// vencido se lê como oferta viva.

/** Os quatro estados, na ordem em que o concurseiro se importa com eles. */
const SITUACOES: { chave: Situacao; rotulo: string; ajuda: string }[] = [
  { chave: "aberta", rotulo: "Inscrições abertas", ajuda: "dá para se inscrever hoje" },
  { chave: "a_abrir", rotulo: "Vão abrir", ajuda: "a inscrição começa em breve" },
  { chave: "a_conferir", rotulo: "A conferir", ajuda: "o prazo do cadastro não dá para ler" },
  { chave: "encerrada", rotulo: "Encerradas", ajuda: "a inscrição já fechou" },
];

type Ordem = "prazo" | "remuneracao" | "prova" | "conferido";

const ORDENS: { chave: Ordem; rotulo: string }[] = [
  { chave: "prazo", rotulo: "Prazo mais próximo" },
  { chave: "remuneracao", rotulo: "Maior remuneração" },
  { chave: "prova", rotulo: "Prova mais próxima" },
  { chave: "conferido", rotulo: "Conferido mais recente" },
];

/** Quantas linhas mostrar antes do "mostrar mais". */
const LOTE = 30;

export default function MapaDeConcursos() {
  const [tudo, setTudo] = useState<Concurso[] | null>(null);
  const [erro, setErro] = useState(false);
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState("");
  const [soFavoritos, setSoFavoritos] = useState(false);
  const [ordem, setOrdem] = useState<Ordem>("prazo");
  const [mostrando, setMostrando] = useState(LOTE);
  const [aberto, setAberto] = useState<Concurso | null>(null);
  // Erro de UMA marcação não é erro de leitura. A cicatriz de 25/08 do
  // Calendário: `setErro(true)` numa escrita que falhou trocava o mês inteiro
  // pela mensagem "não conseguimos carregar", apagando os prazos que o contador
  // estava lendo. Aqui a peça que falha é que informa.
  const [erroDeMarcar, setErroDeMarcar] = useState<string | null>(null);
  // Só para o alerta saber DE QUEM é a dispensa do dia: dois alunos no mesmo
  // navegador não podem herdar o "já vi" um do outro. Na origem a chave do
  // popup de primeira visita é global (`first_visit_popup_seen`), e o segundo
  // aluno nunca o vê.
  const [alunoId, setAlunoId] = useState("anon");
  // Clicar num estado no mapa filtra a lista — que está abaixo da cabine navy
  // INTEIRA, fora da tela. Sem rolar, a pessoa clica, não vê nada mudar e
  // conclui que o clique não funcionou. Achado por Victor em 28/08, junto com
  // o mesmo defeito no Calendário e no Admin › Alunos.
  const listaRef = useRef<HTMLElement | null>(null);

  // `hoje` é congelado no primeiro render: sem isto, a situação de uma linha
  // poderia mudar entre o cálculo do filtro e o da lista se a meia-noite caísse
  // no meio — e a tela mostraria "32 abertas" numa lista de 31.
  const hoje = useMemo(() => hojeIso(new Date()), []);

  // A situação e a UF viajam na URL. Não é preciosismo: o aluno manda "olha os
  // concursos abertos no Paraná" para o colega, e o botão de voltar do navegador
  // desfaz o filtro em vez de sair da ferramenta. É o mesmo motivo de a aba do
  // HUB DP ser sub-rota.
  const situacoesAtivas = useMemo<Set<Situacao>>(() => {
    const bruto = params.get("situacao");
    if (!bruto) return new Set<Situacao>(["aberta", "a_abrir"]);
    const pedidas = bruto.split(",").filter((s): s is Situacao =>
      SITUACOES.some((x) => x.chave === s),
    );
    return new Set<Situacao>(pedidas.length ? pedidas : ["aberta", "a_abrir"]);
  }, [params]);

  const ufAtiva = useMemo(() => {
    const uf = (params.get("uf") ?? "").toUpperCase();
    return (UFS as readonly string[]).includes(uf) ? uf : null;
  }, [params]);

  useEffect(() => {
    void idDaSessao().then(setAlunoId);
  }, []);

  // Rola até a lista quando o aluno escolhe um estado. Só quando ESCOLHE:
  // limpar o filtro não é escolher um resultado, e o primeiro render é a
  // abertura da tela.
  useRolarAoEscolher(listaRef, ufAtiva);

  useEffect(() => {
    let vivo = true;
    lerCatalogo(hoje).then((c) => {
      if (!vivo) return;
      if (!c) setErro(true);
      else setTudo(c);
    });
    return () => {
      vivo = false;
    };
  }, [hoje]);

  function trocarSituacao(s: Situacao) {
    const novo = new Set(situacoesAtivas);
    if (novo.has(s)) novo.delete(s);
    else novo.add(s);
    const p = new URLSearchParams(params);
    // Nenhuma marcada volta ao padrão em vez de mostrar zero linhas: chip que o
    // aluno desmarcou sem querer não pode esvaziar a tela.
    if (novo.size === 0) p.delete("situacao");
    else p.set("situacao", [...novo].join(","));
    setParams(p, { replace: true });
    setMostrando(LOTE);
  }

  function trocarUf(uf: string | null) {
    const p = new URLSearchParams(params);
    if (!uf || uf === ufAtiva) p.delete("uf");
    else p.set("uf", uf);
    setParams(p, { replace: true });
    setMostrando(LOTE);
  }

  async function alternarFavorito(c: Concurso) {
    if (!tudo) return;
    const novo = !c.favorito;
    // Otimista, mas com volta atrás: a origem era otimista e ENGOLIA o erro, o
    // que deixava o coração cheio na tela e nada no banco — o favorito sumia no
    // recarregamento seguinte, sem nenhum sinal.
    setTudo(tudo.map((x) => (x.id === c.id ? { ...x, favorito: novo } : x)));
    setErroDeMarcar(null);
    const msg = await favoritar(c.id, novo);
    if (msg) {
      setTudo((atual) =>
        atual ? atual.map((x) => (x.id === c.id ? { ...x, favorito: c.favorito } : x)) : atual,
      );
      setErroDeMarcar(
        novo
          ? "Não conseguimos salvar este concurso agora. Os prazos continuam aqui — tente de novo em alguns instantes."
          : "Não conseguimos remover este concurso agora. Tente de novo em alguns instantes.",
      );
    }
  }

  if (erro) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
        <p className="surface p-6 text-sm text-tinta">
          Não conseguimos carregar o mapa de concursos agora. Recarregue a página em alguns
          instantes.
        </p>
      </div>
    );
  }
  if (!tudo) return <Carregando />;

  // ---------------------------------------------------------------------
  // Os agregados — todos sobre o conjunto INTEIRO
  // ---------------------------------------------------------------------
  const porSituacao = new Map<Situacao, number>();
  for (const c of tudo) porSituacao.set(c.situacao, (porSituacao.get(c.situacao) ?? 0) + 1);

  const abertas = tudo.filter((c) => c.situacao === "aberta");
  const abertasPorUf = new Map<string, number>();
  for (const c of abertas) abertasPorUf.set(c.uf, (abertasPorUf.get(c.uf) ?? 0) + 1);

  const vagasAbertas = abertas.reduce((s, c) => s + c.vagasImediatas, 0);
  const fechamEm7 = abertas.filter(
    (c) => c.janela && diasAte(hoje, c.janela.fim) <= 7,
  ).length;
  const provasProximas = tudo.filter(
    (c) => c.dataProva && c.dataProva >= hoje && diasAte(hoje, c.dataProva) <= 30,
  ).length;
  const favoritos = tudo.filter((c) => c.favorito).length;
  const comDataFutura = tudo.filter((c) => c.dataProva && c.dataProva >= hoje).length;
  const conferenciaMaisNova = tudo
    .map((c) => c.conferidoEm)
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);

  const visiveis = tudo
    .filter((c) => {
      if (!situacoesAtivas.has(c.situacao)) return false;
      if (ufAtiva && c.uf !== ufAtiva) return false;
      if (soFavoritos && !c.favorito) return false;
      if (busca.trim()) {
        const q = paraBusca(busca);
        const onde = paraBusca(
          `${c.orgao} ${c.cargo} ${c.banca ?? ""} ${c.cidade ?? ""} ${c.uf}`,
        );
        if (!onde.includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (ordem) {
        case "remuneracao":
          return (b.remuneracao ?? -1) - (a.remuneracao ?? -1);
        case "prova":
          // Sem data vai para o fim, e o desempate é o ÓRGÃO — nunca o id.
          // Desempate por uuid é sorteio, e a cicatriz da `vw_contrato_a_assinar`
          // é justamente um teste que passava numa rodada e falhava na outra.
          return (
            (a.dataProva ?? "9999").localeCompare(b.dataProva ?? "9999") ||
            a.orgao.localeCompare(b.orgao, "pt-BR")
          );
        case "conferido":
          return (
            (b.conferidoEm ?? "0000").localeCompare(a.conferidoEm ?? "0000") ||
            a.orgao.localeCompare(b.orgao, "pt-BR")
          );
        case "prazo":
        default:
          return (
            (a.janela?.fim ?? "9999").localeCompare(b.janela?.fim ?? "9999") ||
            a.orgao.localeCompare(b.orgao, "pt-BR")
          );
      }
    });

  const ufsComAlgo = [...abertasPorUf.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
      <Cabine
        rotulo="FERRAMENTA"
        titulo="Mapa de Concursos Contábeis"
        descricao={
          <>
            As vagas públicas de contabilidade que a equipe acompanha, com o prazo de
            inscrição, a data da prova e o link do edital. Marque os que você quer
            acompanhar e o prazo fica à vista.
          </>
        }
        fatos={
          <>
            <TileDeFato
              rotulo="Inscrições abertas"
              valor={String(abertas.length)}
              apoio={
                abertas.length > 0
                  ? `em ${abertasPorUf.size} ${abertasPorUf.size === 1 ? "estado" : "estados"}`
                  : "nenhuma hoje"
              }
            />
            {fechamEm7 > 0 && (
              <TileDeFato
                rotulo="Fecham em 7 dias"
                valor={String(fechamEm7)}
                apoio="não deixe para depois"
              />
            )}
            {vagasAbertas > 0 && (
              <TileDeFato
                rotulo="Vagas imediatas"
                valor={String(vagasAbertas)}
                apoio="fora o cadastro de reserva"
              />
            )}
            {provasProximas > 0 && (
              <TileDeFato
                rotulo="Provas em 30 dias"
                valor={String(provasProximas)}
                apoio="de concursos da lista"
              />
            )}
            {favoritos > 0 && (
              <TileDeFato
                rotulo="Você acompanha"
                valor={String(favoritos)}
                apoio={favoritos === 1 ? "concurso" : "concursos"}
              />
            )}
          </>
        }
        acao={
          // O grupo de vagas. Na origem ele é a ÚNICA chamada para ação da
          // ferramenta, e aparece em quatro lugares — cabeçalho, barra de baixo,
          // popup de primeira visita e um botão flutuante —, sendo que dois deles
          // são componentes órfãos que nada importa. Aqui é um lugar só.
          //
          // Em pill LARANJA e não no verde do WhatsApp: a doutrina da casa diz
          // que CTA é pill laranja com texto azul escuro, validada contra o
          // Figma. Verde de terceiro dentro do navy da marca é elemento
          // estranho — a função é a mesma, a pintura é daqui.
          <a
            href={GRUPO_DE_VAGAS}
            target="_blank"
            rel="noreferrer"
            data-grupo
            className="pill-cta inline-flex items-center gap-2"
          >
            Entrar no grupo de vagas
            <span aria-hidden="true">→</span>
            <span className="sr-only">(abre em outra aba)</span>
          </a>
        }
        rodape={<AbasDoMapa provas={comDataFutura} />}
      >
        {/* O MAPA dentro do território navy, com a legenda da escala. */}
        <div className="mt-7 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div>
            <MapaDoBrasilSvg
              ufs={UFS as unknown as string[]}
              corDaUf={(uf) => corDoDegrau(abertasPorUf.get(uf) ?? 0)}
              tituloDaUf={(uf) => {
                const n = abertasPorUf.get(uf) ?? 0;
                return `${uf} — ${n === 0 ? "nenhuma inscrição aberta" : n === 1 ? "1 inscrição aberta" : `${n} inscrições abertas`}`;
              }}
              ativo={ufAtiva}
              onEscolher={trocarUf}
              corDoTraco="var(--cf-mapa-traco)"
            />
            <div className="mt-3">
              <LegendaDaEscala titulo="Inscrições abertas por estado" />
            </div>
          </div>

          {/* Os chips de estado: a NAVEGAÇÃO de verdade. O mapa é o atalho. */}
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-navy-mute">
              Escolher um estado
            </h2>
            {ufsComAlgo.length === 0 ? (
              <p className="pt-2 text-sm text-navy-ink">
                Nenhum estado tem inscrição aberta hoje. Os chips voltam quando a equipe
                cadastrar um edital novo.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5 pt-2.5">
                {ufsComAlgo.map(([uf, n]) => (
                  <button
                    key={uf}
                    type="button"
                    onClick={() => trocarUf(uf)}
                    aria-pressed={ufAtiva === uf}
                    className={`rounded-full px-2.5 py-1 text-xs font-bold tabular-nums transition ${
                      ufAtiva === uf
                        ? "bg-laranja text-navy-ink"
                        : "bg-white/10 text-white hover:bg-white/20"
                    }`}
                  >
                    {uf} <span className="font-semibold opacity-75">{n}</span>
                  </button>
                ))}
              </div>
            )}
            {ufAtiva && (
              <button
                type="button"
                onClick={() => trocarUf(null)}
                className="mt-3 text-xs font-semibold text-laranja-claro underline"
              >
                Mostrar todos os estados
              </button>
            )}
            {conferenciaMaisNova && (
              <p className="mt-5 border-t border-navy-mute/20 pt-4 text-xs leading-relaxed text-navy-mute">
                Catálogo com {tudo.length} concursos. A conferência mais recente da equipe é
                de {dataLonga(conferenciaMaisNova)} — cada linha diz a sua.
              </p>
            )}
          </div>
        </div>
      </Cabine>

      {/* ------------------------------------------------------------------ */}
      {/*
        As duas visões são SUB-ROTA, não `useState`: o aluno volta pelo botão do
        navegador, compartilha o link de uma delas, e recarregar não o joga para a
        primeira. Mesma decisão do HUB DP.

        No app de origem são duas rotas de TOPO (`/` e `/calendario`), e o rótulo
        delas diverge entre os dois lugares que as apontam — o cabeçalho chama de
        "Calendário" e a barra de baixo chama de "Provas". Aqui o rótulo mora num
        lugar só, em `AbasDoMapa`.
      */}
      <Routes>
        <Route
          path="provas"
          element={<AgendaDeProvas concursos={tudo} hoje={hoje} onAbrir={setAberto} />}
        />
        <Route
          path="*"
          element={
            <>
              <AlertaDePrazo
                concursos={tudo}
                hoje={hoje}
                alunoId={alunoId}
                onAbrir={setAberto}
              />
              <section
                role="group"
                aria-labelledby="titulo-lista"
                ref={listaRef}
                className="mt-8"
              >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 id="titulo-lista" className="font-display text-xl font-bold text-titulo">
              {visiveis.length}{" "}
              {visiveis.length === 1 ? "concurso" : "concursos"}
              {ufAtiva && ` em ${ufAtiva}`}
            </h2>
            <p className="pt-1 text-sm text-cinza">
              De {tudo.length} no catálogo. Escolha abaixo o que quer ver.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-cinza">Ordenar por</span>
            <select
              value={ordem}
              onChange={(e) => setOrdem(e.target.value as Ordem)}
              className="rounded-full border border-borda bg-superficie px-3 py-1.5 text-sm font-semibold text-tinta"
            >
              {ORDENS.map((o) => (
                <option key={o.chave} value={o.chave}>
                  {o.rotulo}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Os chips de SITUAÇÃO, com a contagem de cada um à vista — inclusive a
            das encerradas, que o filtro padrão esconde. */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {SITUACOES.map((s) => {
            const n = porSituacao.get(s.chave) ?? 0;
            const ligado = situacoesAtivas.has(s.chave);
            return (
              <button
                key={s.chave}
                type="button"
                onClick={() => trocarSituacao(s.chave)}
                aria-pressed={ligado}
                title={s.ajuda}
                className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                  ligado
                    ? "bg-azul text-white"
                    : "bg-chip text-cinza hover:text-tinta"
                } ${n === 0 ? "opacity-60" : ""}`}
              >
                {s.rotulo} <span className="tabular-nums opacity-80">{n}</span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => {
              setSoFavoritos((v) => !v);
              setMostrando(LOTE);
            }}
            aria-pressed={soFavoritos}
            className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${
              soFavoritos ? "bg-laranja text-navy-ink" : "bg-chip text-cinza hover:text-tinta"
            }`}
          >
            Só os que eu acompanho{" "}
            <span className="tabular-nums opacity-80">{favoritos}</span>
          </button>
        </div>

        <div className="mt-3">
          <label className="block">
            <span className="sr-only">Buscar por órgão, cargo, banca ou cidade</span>
            <input
              type="search"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setMostrando(LOTE);
              }}
              placeholder="Buscar órgão, cargo, banca ou cidade…"
              className="w-full rounded-full border border-borda bg-superficie px-4 py-2 text-sm text-tinta placeholder:text-cinza focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40 sm:max-w-md"
            />
          </label>
        </div>

        {erroDeMarcar && (
          <p
            role="status"
            className="mt-3 rounded-cf bg-erro-fundo px-4 py-3 text-sm text-erro-tinta"
          >
            {erroDeMarcar}
          </p>
        )}

        {visiveis.length === 0 ? (
          <p className="surface mt-4 p-6 text-sm text-cinza">
            {soFavoritos
              ? "Você ainda não marcou nenhum concurso desta lista."
              : busca.trim()
                ? `Nada encontrado para "${busca.trim()}" com os filtros de agora.`
                : "Nenhum concurso com os filtros de agora. Ligue outro chip acima."}
          </p>
        ) : (
          <>
            <ul className="mt-4 space-y-2">
              {visiveis.slice(0, mostrando).map((c) => (
                <LinhaDoConcurso
                  key={c.id}
                  c={c}
                  hoje={hoje}
                  onAbrir={() => setAberto(c)}
                  onFavoritar={() => void alternarFavorito(c)}
                />
              ))}
            </ul>
            {visiveis.length > mostrando && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setMostrando((n) => n + LOTE)}
                  className="btn-contorno"
                >
                  Mostrar mais {Math.min(LOTE, visiveis.length - mostrando)} de{" "}
                  {visiveis.length - mostrando}
                </button>
              </div>
            )}
          </>
        )}
              </section>
            </>
          }
        />
      </Routes>

      <Detalhe c={aberto} hoje={hoje} onFechar={() => setAberto(null)} />
    </div>
  );
}

/**
 * O selo da situação — texto, nunca só cor.
 *
 * `nomeandoOAssunto` faz o selo dizer que fala da INSCRIÇÃO. Isto nasceu de um
 * defeito que Victor achou na tela em 31/08: ele abriu uma PROVA na agenda e leu
 * "encerrada" numa prova que ainda vai acontecer (CREF-21/MA: inscrição de 11/06
 * a 05/08, prova em 13/09). O dado estava certo — todo valor do modal vem do
 * concurso selecionado — e o RÓTULO era ambíguo.
 *
 * Na LISTA o selo pode ser curto: a lista é ordenada por prazo de inscrição, os
 * chips acima dela dizem "Inscrições abertas", e o assunto está no contexto. No
 * MODAL não há contexto — ele abre dos dois lugares —, então lá o selo se
 * apresenta.
 *
 * É a mesma família da cicatriz do "Limite com a prorrogação" do HUB DP: o que a
 * tela não diz, o olho preenche com a suposição errada.
 */
function SeloDeSituacao({
  c,
  hoje,
  nomeandoOAssunto = false,
}: {
  c: Concurso;
  hoje: string;
  nomeandoOAssunto?: boolean;
}) {
  const pre = nomeandoOAssunto ? "inscrições " : "";
  if (c.situacao === "aberta" && c.janela) {
    const dias = diasAte(hoje, c.janela.fim);
    const u = urgenciaDoPrazo(dias);
    const classe =
      u?.nivel === "hoje" || u?.nivel === "critico"
        ? "bg-erro-fundo text-erro-tinta"
        : u?.nivel === "atencao"
          ? "bg-alerta-fundo text-alerta-tinta"
          : "bg-aviso-fundo text-aviso-tinta";
    return (
      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${classe}`}>
        {nomeandoOAssunto
          ? `inscrições até ${dataCurta(c.janela.fim)}${u ? ` — ${u.texto}` : ""}`
          : (u?.texto ?? `até ${dataCurta(c.janela.fim)}`)}
      </span>
    );
  }
  if (c.situacao === "a_abrir" && c.janela) {
    return (
      <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-bold text-chip-tinta">
        {pre}abre{nomeandoOAssunto ? " em " : " "}
        {dataCurta(c.janela.inicio)}
      </span>
    );
  }
  if (c.situacao === "a_conferir") {
    // A AUSÊNCIA de prazo não pode ser silêncio. A cicatriz da aba Prazos da
    // Admissão: quando a linha condicional carrega um aviso, o ramo em que ela
    // não aparece precisa de texto próprio — esconder é o que o olho lê como
    // "está tudo bem".
    return (
      <span className="rounded-full bg-alerta-fundo px-2 py-0.5 text-xs font-bold text-alerta-tinta">
        {nomeandoOAssunto ? "prazo de inscrição a conferir no edital" : "confira no edital"}
      </span>
    );
  }
  return (
    <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-cinza">
      {/* Concordância: "encerrada" no singular quando o selo é curto (o sujeito
          oculto é a inscrição), "encerradas" quando ele nomeia o plural. A
          primeira versão colava o prefixo no singular e saía "inscrições
          encerrada em 06/07". */}
      {nomeandoOAssunto
        ? `inscrições encerradas${c.janela ? ` em ${dataCurta(c.janela.fim)}` : ""}`
        : "encerrada"}
    </span>
  );
}

/**
 * O selo da PROVA — o fato que quem vem da agenda foi buscar.
 *
 * Existe porque o selo de inscrição, sozinho, respondia a pergunta errada nesse
 * caminho: um concurso com inscrição encerrada e prova em duas semanas mostrava
 * só "encerrada", e a prova é justamente o que ainda vai acontecer.
 */
function SeloDaProva({ c, hoje }: { c: Concurso; hoje: string }) {
  if (!c.dataProva) {
    return (
      <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-cinza">
        prova sem data no cadastro
      </span>
    );
  }
  const dias = diasAte(hoje, c.dataProva);
  if (dias < 0) {
    return (
      <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-cinza">
        prova realizada em {dataCurta(c.dataProva)}
      </span>
    );
  }
  const classe =
    dias <= 3
      ? "bg-erro-fundo text-erro-tinta"
      : dias <= 7
        ? "bg-alerta-fundo text-alerta-tinta"
        : "bg-aviso-fundo text-aviso-tinta";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${classe}`}>
      prova {dias === 0 ? "é hoje" : dias === 1 ? "é amanhã" : `em ${dias} dias`} —{" "}
      {dataCurta(c.dataProva)}
    </span>
  );
}

const REAIS = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  maximumFractionDigits: 0,
});

function LinhaDoConcurso({
  c,
  hoje,
  onAbrir,
  onFavoritar,
}: {
  c: Concurso;
  hoje: string;
  onAbrir: () => void;
  onFavoritar: () => void;
}) {
  return (
    <li className="surface flex items-start gap-3 p-4">
      <button
        type="button"
        onClick={onFavoritar}
        aria-pressed={c.favorito}
        data-favoritar={c.id}
        title={c.favorito ? "Deixar de acompanhar" : "Acompanhar este concurso"}
        className={`mt-0.5 grid size-9 shrink-0 place-items-center rounded-full transition ${
          c.favorito
            ? "bg-laranja text-navy-ink"
            : "bg-tile text-cinza hover:text-laranja-texto"
        }`}
      >
        <span aria-hidden="true" className="text-base leading-none">
          {c.favorito ? "★" : "☆"}
        </span>
        <span className="sr-only">
          {c.favorito ? "Deixar de acompanhar" : "Acompanhar"} {c.orgao}
        </span>
      </button>

      {/*
        A LINHA INTEIRA é o botão que abre o detalhe, e não um "Ver detalhes" ao
        lado. Duas razões medidas na captura de 27/08: com a quarta linha e o
        botão próprio, 30 linhas davam **7.679px no celular** (8,5 telas) para 30
        itens, e o instrumento acusou "Conferido em 06/08/2026Ver detalhes"
        repetido 12 vezes — a mesma informação dita duas vezes por linha, que é a
        contagem que reduziu o IR Pro de 8.249px para 1.120px.

        E `banca` saiu da lista: ela é o nome de uma fundação ("Instituto
        Consulplan", "Fundação Fafipa") e faz a linha quebrar no celular sem
        ajudar a escolher. Continua no detalhe, e continua na BUSCA.
      */}
      <button
        type="button"
        onClick={onAbrir}
        className="flex min-w-0 flex-1 flex-col text-left sm:flex-row sm:items-start sm:gap-5"
        data-abrir={c.id}
      >
        {/*
          Os FATOS ficam AO LADO do título nas telas largas, não embaixo.
          Medido na captura de 27/08: com tudo empilhado, cada linha usava um
          terço da largura de 1152px e a lista dava 5.131px — o desperdício era
          HORIZONTAL, e a página crescia por causa dele.

          E é uma linha por concurso, não duas colunas de concursos: a ordenação
          padrão é "prazo mais próximo", e o aluno lê de cima para baixo até achar
          um prazo que dê tempo. Grade de duas colunas quebra a ordem de leitura
          de uma lista ORDENADA — funciona no índice do Gerenciador, onde se
          procura um número, e não aqui.
        */}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-titulo">{c.orgao}</span>
            <SeloDeSituacao c={c} hoje={hoje} />
          </span>
          <span className="block pt-0.5 text-sm text-tinta">{c.cargo}</span>
        </span>

        {/*
          No CELULAR isto é uma linha de texto que quebra sozinha; a partir de
          `sm` é uma coluna à direita, com a segunda metade numa linha própria.
          A primeira tentativa usou `block sm:inline` e ficou ao contrário — o
          celular subiu de 7.079px para 7.519px porque cada fato virou uma linha
          lá também. O instrumento pegou; a foto sozinha não pegaria.
        */}
        <span className="shrink-0 text-sm text-cinza sm:w-[19rem] sm:text-right">
          {/* `data-uf` é ESCOPO de prova, não estilo. Sem ele a asserção lia a UF
              por regex sobre o texto da linha, e `\b[A-Z]{2}\b` casava o "TJ" de
              "TJ/PR" antes da UF — a cicatriz das sete etiquetas iguais. */}
          <span data-uf={c.uf} className="font-semibold text-tinta">
            {c.uf}
          </span>
          {c.cidade && ` · ${c.cidade}`}
          {c.remuneracao !== null && ` · ${REAIS.format(c.remuneracao)}`}
          {/* "CR" é cadastro de reserva: zero vaga imediata, e isso se diz com
              letra. São 83 dos 256 concursos — um terço do catálogo. */}
          {c.vagasImediatas > 0
            ? ` · ${c.vagasImediatas} ${c.vagasImediatas === 1 ? "vaga" : "vagas"}`
            : c.vagasTexto
              ? " · cadastro de reserva"
              : ""}
          <span className="sm:block sm:pt-0.5 sm:text-xs">
            <span className="sm:hidden"> · </span>
            {c.dataProva && `prova ${dataCurta(c.dataProva)}`}
            {/* A procedência anda junto do dado, em forma curta. A longa está no
                detalhe e na cabine. */}
            {c.conferidoEm && `${c.dataProva ? " · " : ""}conf. ${dataCurta(c.conferidoEm)}`}
          </span>
        </span>
      </button>
    </li>
  );
}

/**
 * O detalhe, em `<dialog>` nativo — o padrão do `ModalVigencias`.
 *
 * O link do edital abre em outra aba: é outro sistema (99 portais de banca
 * diferentes), e o `sr-only` "abre em outra aba" existe por isso. `rel="noreferrer"`
 * é o padrão da casa.
 */
function Detalhe({
  c,
  hoje,
  onFechar,
}: {
  c: Concurso | null;
  hoje: string;
  onFechar: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (c && !d.open) d.showModal();
    if (!c && d.open) d.close();
  }, [c]);

  // Um `<dialog>` SÓ, sempre no DOM. A primeira versão fazia
  // `if (!c) return <dialog className="hidden" />`, o que TROCA o elemento a cada
  // abertura e fechamento: o efeito acima então guarda a referência de um
  // elemento que já saiu do DOM, e `close()` roda no lugar errado. Funcionava por
  // sorte (remover o nó fecha o modal); agora funciona por desenho.
  if (!c) return <dialog ref={ref} aria-hidden="true" className="hidden" />;

  const linhas: [string, string][] = [
    ["Órgão", c.orgao],
    ["Cargo", c.cargo],
    ["Estado", c.uf],
    ...(c.cidade ? ([["Lotação", c.cidade]] as [string, string][]) : []),
    // O local de prova só existe quando ACRESCENTA algo a "cidade/UF" — a carga
    // não grava a cópia derivável. Então, quando ele aparece, ele informa.
    ...(c.localProva ? ([["Local de prova", c.localProva]] as [string, string][]) : []),
    ...(c.banca ? ([["Banca", c.banca]] as [string, string][]) : []),
    ...(c.vagasTexto ? ([["Vagas", c.vagasTexto]] as [string, string][]) : []),
    ...(c.remuneracaoTexto ? ([["Remuneração", c.remuneracaoTexto]] as [string, string][]) : []),
    ...(c.jornada ? ([["Jornada", c.jornada]] as [string, string][]) : []),
    // O TEXTO da inscrição, e não as datas: ele carrega as HORAS, que decidem se
    // dá tempo de inscrever hoje.
    [
      "Inscrições",
      c.inscricaoTexto ?? "não informado no cadastro",
    ],
    ["Prova", c.dataProva ? dataLonga(c.dataProva) : "sem data definida"],
    ...(c.observacoes ? ([["Observações", c.observacoes]] as [string, string][]) : []),
    [
      "Conferido pela equipe",
      c.conferidoEm ? dataLonga(c.conferidoEm) : "sem data de conferência",
    ],
  ];

  return (
    <dialog
      ref={ref}
      onClose={onFechar}
      onClick={(e) => {
        if (e.target === ref.current) onFechar();
      }}
      /*
       * `m-auto` é o que CENTRALIZA, e a ausência dele foi um defeito real: o
       * modal abria encostado no canto superior esquerdo (visto na tela em
       * 31/08).
       *
       * O `<dialog>` nativo se centraliza com as regras do navegador —
       * `inset: 0` mais `margin: auto` —, e o reset de CSS do hub zera a margem
       * de tudo. Com `margin: 0`, o `inset: 0` resolve para o canto. Os outros
       * três modais da casa (`ModalVigencias`, `SimuladorMeiUi`,
       * `LegalizaRedator`) já traziam `m-auto`; este nasceu sem, e a prova de
       * tela não olhava POSIÇÃO — ela contava `dialog[open]`, que é verdadeiro
       * no canto também.
       *
       * `max-h` aqui e `overflow-y-auto` na lista do meio, não no `<dialog>`:
       * assim o cabeçalho e o botão "Abrir o edital" ficam presos, e é a lista
       * de 13 campos que rola. Sem isso, numa tela baixa o CTA — que é o ponto
       * do modal — cai fora do alcance.
       */
      className="m-auto flex max-h-[min(90dvh,44rem)] w-[min(92vw,40rem)] flex-col rounded-cf-lg bg-superficie p-0 text-tinta shadow-2xl backdrop:bg-navy-ink/60"
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-borda px-5 py-4">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold text-titulo">{c.orgao}</h2>
          <p className="pt-0.5 text-sm text-cinza">{c.cargo}</p>
        </div>
        <div className="flex shrink-0 items-start gap-2">
          {/* Os DOIS fatos, cada um dizendo do que fala. O modal abre da lista
              (onde o assunto é a inscrição) e da agenda (onde é a prova), e sem
              o rótulo o mesmo selo responde a pergunta errada num dos dois. */}
          <div className="flex flex-col items-end gap-1">
            <SeloDeSituacao c={c} hoje={hoje} nomeandoOAssunto />
            <SeloDaProva c={c} hoje={hoje} />
          </div>
          <button
            type="button"
            onClick={onFechar}
            className="grid size-8 place-items-center rounded-full bg-chip text-cinza hover:text-tinta"
          >
            <span aria-hidden="true">×</span>
            <span className="sr-only">Fechar</span>
          </button>
        </div>
      </div>

      <dl className="grid min-h-0 flex-1 gap-x-5 gap-y-3 overflow-y-auto px-5 py-5 sm:grid-cols-2">
        {linhas.map(([rotulo, valor]) => (
          <div key={rotulo} className={rotulo === "Observações" ? "sm:col-span-2" : undefined}>
            <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-cinza">
              {rotulo}
            </dt>
            <dd className="pt-0.5 text-sm text-tinta">{valor}</dd>
          </div>
        ))}
      </dl>

      {c.linkEdital ? (
        <div className="shrink-0 border-t border-borda px-5 py-4">
          <a
            href={c.linkEdital}
            target="_blank"
            rel="noreferrer"
            className="pill-cta inline-flex items-center gap-2"
          >
            Abrir o edital
            <span aria-hidden="true">→</span>
            <span className="sr-only">(abre em outra aba, no site da banca)</span>
          </a>
          <p className="pt-2 text-xs text-cinza">
            O edital é a fonte oficial. As datas aqui são o que a equipe leu nele em{" "}
            {c.conferidoEm ? dataLonga(c.conferidoEm) : "data não registrada"}; confirme antes
            de pagar a taxa.
          </p>
        </div>
      ) : (
        // Ausência que carrega aviso precisa de texto próprio.
        <div className="shrink-0 border-t border-borda px-5 py-4">
          <p className="text-sm text-cinza">
            Este cadastro não tem o link do edital. Procure pelo nome do órgão no site da
            banca.
          </p>
        </div>
      )}
    </dialog>
  );
}
